import { useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Plus, Trash2, ArrowUpCircle, ArrowDownCircle, CalendarIcon, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const empty = { type: "ingreso", category: "", amount: "", event_id: "none", description: "", date: undefined };

export default function FinanceManager({ transactions, events, reload }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const eventName = (id) => events.find((e) => e.id === id)?.name || "General";

  const openNew = () => {
    setForm(empty);
    setEditingId(null);
    setOpen(true);
  };

  const openEdit = (tx) => {
    let dateObj;
    if (tx.date) {
      const [y, m, d] = tx.date.split("-").map(Number);
      dateObj = new Date(y, m - 1, d);
    }
    setForm({
      type: tx.type,
      category: tx.category,
      amount: String(tx.amount),
      event_id: tx.event_id || "none",
      description: tx.description || "",
      date: dateObj,
    });
    setEditingId(tx.id);
    setOpen(true);
  };

  const submit = async () => {
    if (!form.category || !form.amount || !form.date) {
      toast.error("Categoría, monto y fecha son obligatorios");
      return;
    }
    setSaving(true);
    const payload = {
      type: form.type,
      category: form.category,
      amount: parseFloat(form.amount),
      event_id: form.event_id === "none" ? null : form.event_id,
      description: form.description,
      date: format(form.date, "yyyy-MM-dd"),
    };
    try {
      if (editingId) {
        await api.put(`/transactions/${editingId}`, payload);
        toast.success("Transacción actualizada");
      } else {
        await api.post("/transactions", payload);
        toast.success("Transacción registrada");
      }
      setForm(empty);
      setEditingId(null);
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Transacción eliminada");
      reload();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  return (
    <div data-testid="finance-panel">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-serif text-3xl">Finanzas y Costos</h2>
        <Button
          data-testid="new-transaction-button"
          onClick={openNew}
          className="rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
        >
          <Plus className="mr-2 h-4 w-4" /> Nueva Transacción
        </Button>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="border-border bg-card sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">
                {editingId ? "Editar Transacción" : "Registrar Transacción"}
              </DialogTitle>
              <DialogDescription>Añade un ingreso o gasto, opcionalmente vinculado a un evento.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="tx-type-select" className="bg-muted">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="border-border bg-card">
                      <SelectItem value="ingreso">Ingreso</SelectItem>
                      <SelectItem value="gasto">Gasto</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Monto (USD)</Label>
                  <Input
                    type="number"
                    data-testid="tx-amount-input"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    placeholder="0.00"
                    className="bg-muted"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Input
                  data-testid="tx-category-input"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  placeholder="Ofrenda, Sonido, Alquiler…"
                  className="bg-muted"
                />
              </div>
              <div className="space-y-2">
                <Label>Evento (opcional)</Label>
                <Select value={form.event_id} onValueChange={(v) => setForm({ ...form, event_id: v })}>
                  <SelectTrigger data-testid="tx-event-select" className="bg-muted">
                    <SelectValue placeholder="General" />
                  </SelectTrigger>
                  <SelectContent className="border-border bg-card">
                    <SelectItem value="none">General (sin evento)</SelectItem>
                    {events.map((ev) => (
                      <SelectItem key={ev.id} value={ev.id}>
                        {ev.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fecha</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      data-testid="tx-date-trigger"
                      className="w-full justify-start border-border bg-muted font-normal hover:bg-muted"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-primary" />
                      {form.date ? format(form.date, "PPP", { locale: es }) : "Selecciona una fecha"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-border bg-card p-0" align="start">
                    <Calendar mode="single" selected={form.date} onSelect={(d) => setForm({ ...form, date: d })} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Input
                  data-testid="tx-description-input"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="bg-muted"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={submit}
                disabled={saving}
                data-testid="save-transaction-button"
                className="rounded-full bg-primary text-primary-foreground hover:bg-[#E5B54B]"
              >
                {saving ? "Guardando…" : editingId ? "Actualizar" : "Registrar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      {transactions.length === 0 ? (
        <Card className="border-dashed border-border bg-card p-16 text-center text-muted-foreground">
          No hay transacciones registradas todavía.
        </Card>
      ) : (
        <Card className="border-border bg-card">
          <Table data-testid="transactions-table">
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="overline">Tipo</TableHead>
                <TableHead className="overline">Categoría</TableHead>
                <TableHead className="overline">Evento</TableHead>
                <TableHead className="overline">Fecha</TableHead>
                <TableHead className="overline text-right">Monto</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((tx) => (
                <TableRow key={tx.id} data-testid={`tx-row-${tx.id}`} className="border-border">
                  <TableCell>
                    {tx.type === "ingreso" ? (
                      <Badge className="gap-1 border-0 bg-[hsl(145_55%_45%)]/15 text-[hsl(145_55%_55%)]">
                        <ArrowUpCircle className="h-3 w-3" /> Ingreso
                      </Badge>
                    ) : (
                      <Badge className="gap-1 border-0 bg-primary/15 text-primary">
                        <ArrowDownCircle className="h-3 w-3" /> Gasto
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{tx.category}</TableCell>
                  <TableCell className="text-muted-foreground">{eventName(tx.event_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{tx.date}</TableCell>
                  <TableCell
                    className="text-right font-semibold"
                    style={{ color: tx.type === "ingreso" ? "hsl(145 55% 55%)" : "hsl(45 85% 60%)" }}
                  >
                    {tx.type === "ingreso" ? "+" : "−"}
                    {money(tx.amount)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={() => openEdit(tx)}
                        data-testid={`edit-tx-${tx.id}`}
                        className="text-muted-foreground transition-colors hover:text-primary"
                        title="Editar transacción"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(tx.id)}
                        data-testid={`delete-tx-${tx.id}`}
                        className="text-muted-foreground transition-colors hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
